/**
 * Mockups — HTML the studio shares with a client for feedback (session 48,
 * `.docs/48-mockups.md`).
 *
 * A mockup is one design across every version of it that was uploaded. A version is never
 * replaced: the next upload is the next version, the client can open any of them, and a
 * comment stays with the version it was about. The review is per version and speaks the
 * files review cycle's vocabulary (`FILE_REVIEW_STATES`), because it is the same question —
 * "is this right?" — asked of a different thing.
 */
import { z } from 'zod';
import { FileReviewSchema, FileReviewStateSchema } from './files.js';
import { Rfc3339Schema } from './primitives.js';
import { ClientSlugSchema, MockupRefSchema, WorkOrderRefSchema } from './refs.js';
/* ------------------------------------------------------------------- the limits */
/** The most one upload may carry: one `.html` file or one `.zip`. nginx is configured to match. */
export const MOCKUP_UPLOAD_MAX_BYTES = 50 * 1024 * 1024;
/** The most a zip may unpack to, all its files together. */
export const MOCKUP_UNPACKED_MAX_BYTES = 200 * 1024 * 1024;
/** The most files one version may have. A design system's icon folder is a few hundred. */
export const MOCKUP_MAX_FILES = 1_000;
export const MOCKUP_TITLE_MAX = 120;
export const MOCKUP_NOTE_MAX = 2_000;
export const MOCKUP_COMMENT_MAX = 4_000;
/**
 * The most a version sent **inside a tool call** may be, its files decoded (session 48b). Below
 * the upload's 50 MiB because it travels as JSON — base64 is four bytes for every three — and
 * the MCP door's body limit is 12 MiB. Larger than this is `mockups_create_upload`'s URL.
 */
export const MOCKUP_INLINE_MAX_BYTES = 8 * 1024 * 1024;
/**
 * The most files a version sent inside a tool call may have (session 48b), against an upload's
 * 1,000. Its bytes are written inside the call's transaction — two fsyncs a file, holding one of
 * the pool's connections and the mockup's row lock — so the count is the cost, not the size.
 */
export const MOCKUP_INLINE_MAX_FILES = 100;
/** A guest's name — what the comment is signed with (session 49). */
export const MOCKUP_GUEST_NAME_MAX = 80;
/** The longest CSS selector a pin may carry. A path through a deep page fits in a tenth of it. */
export const MOCKUP_PIN_SELECTOR_MAX = 1_000;
/**
 * The most characters a comment on selected text may be about (session 55) — a long paragraph.
 * A longer selection is a comment on the element it sits in instead.
 */
export const MOCKUP_QUOTE_MAX = 2_000;
/** The widest page a pin may say it was placed on, in CSS pixels — a wall of monitors. */
export const MOCKUP_VIEWPORT_MAX = 10_000;
/**
 * A guest's limits (session 49): comments from one address to one mockup in an hour, and from
 * everybody with the link to one mockup in a day. Counted under the mockup's row lock.
 */
export const MOCKUP_GUEST_HOURLY = 20;
export const MOCKUP_GUEST_DAILY = 200;
/**
 * Where the pin bridge is served from, on the portal's own host (session 49): the SPA's build
 * emits it (`apps/web/scripts/mockup-pins.ts`) and the preview route appends a tag naming it to
 * every HTML page it serves.
 */
export const MOCKUP_PIN_SCRIPT_PATH = '/mockup-pins.js';
/** Where a mockup's link opens: the public viewer, an SPA route outside both portals. */
export const MOCKUP_VIEW_PREFIX = '/view';
/* ----------------------------------------------------------------------- inputs */
export const MockupTitleSchema = z
    .string()
    .trim()
    .min(1)
    .max(MOCKUP_TITLE_MAX)
    .describe('What the design is, as the client will read it — "Homepage redesign".');
export const MockupVersionNoteSchema = z
    .string()
    .trim()
    .max(MOCKUP_NOTE_MAX)
    .default('')
    .describe('What this version is, or what changed since the last one — "Hero shortened, pricing ' +
    'table added". It goes in the mail and beside the version. Empty for none.');
/**
 * One file of a version sent inside a tool call (session 48b) — what a zip entry is to an
 * upload. `bundle.ts` judges its path and type exactly as it judges a zip's.
 */
export const MockupFileInputSchema = z
    .strictObject({
    path: z
        .string()
        .min(1)
        .max(512)
        .describe('Where the file sits in the mockup, relative — "index.html", "css/app.css", ' +
        '"images/hero.png". The page opened is index.html, or the only HTML file.'),
    content: z
        .string()
        .describe('The file: its text, or for an image, font or video its bytes in base64.'),
    encoding: z
        .enum(['utf8', 'base64'])
        .default('utf8')
        .describe('utf8 for text (HTML, CSS, JS, SVG, JSON); base64 for anything binary.'),
})
    .describe('One file of a mockup version.');
/**
 * A version's content, inside the call (session 48b): one self-contained page as `html`, or
 * several files as `files`. Exactly one of the two; `mockups/bundle.ts` `bundleFromContent`
 * says so when neither or both arrive, because a JSON Schema `oneOf` is a shape many clients
 * will not draw. `html` has no `maxLength` on purpose: the same function refuses one over
 * `MOCKUP_INLINE_MAX_BYTES` with a sentence naming the upload, where zod would say "too big".
 */
/** `MOCKUP_INLINE_MAX_BYTES` as a description says it. */
const INLINE_MB = String(MOCKUP_INLINE_MAX_BYTES / 1024 / 1024);
export const MockupContentFields = {
    html: z
        .string()
        .min(1)
        .optional()
        .describe('The whole page, as one self-contained HTML document — its CSS and scripts inline, ' +
        `images inline or by full https:// URL. Up to ${INLINE_MB} MB. Leave out when sending ` +
        'files instead.'),
    files: z
        .array(MockupFileInputSchema)
        .min(1)
        .max(MOCKUP_INLINE_MAX_FILES)
        .optional()
        .describe('The page and everything beside it, file by file, when it is more than one file — up ' +
        `to ${String(MOCKUP_INLINE_MAX_FILES)} files and ${INLINE_MB} MB together. Paths are ` +
        'relative ("images/hero.png", never "/images/hero.png"). Leave out when sending html ' +
        'instead.'),
};
/** A fraction of a box: 0 is its left or top edge, 1 its right or bottom. */
const FractionSchema = z.number().min(0).max(1);
/** A size as a fraction of a box: more than nothing, at most all of it. */
const ExtentSchema = z.number().gt(0).max(1);
/**
 * A spot on one page of one version (session 49, `.docs/49-mockup-pins-and-guests.md`
 * §Pins). The frame's bridge makes one from a click; nobody types it.
 *
 * Session 55 (`.docs/55-mockup-comments.md` §The anchor) adds two more kinds of spot, each
 * optional and each a pair: **an area** (`w`, `h` — `x`/`y` are then its top-left corner) for
 * "this part of the photo", and **text** (`quote`, `quote_start`) for the characters somebody
 * selected. Never both. And `viewport_w`, how wide the page was when the pin was placed.
 */
const MockupPinFields = z.strictObject({
    page: z
        .string()
        .min(1)
        .max(512)
        .describe('The page inside the version the spot is on — "index.html", "about/index.html".'),
    selector: z
        .string()
        .min(1)
        .max(MOCKUP_PIN_SELECTOR_MAX)
        .describe('A CSS selector for the element that was clicked — for a comment on text, the element ' +
        'the text is in.'),
    x: FractionSchema.describe('Where across that element, 0 to 1 — for an area, its left edge; for text, where it starts.'),
    y: FractionSchema.describe('Where down that element, 0 to 1 — for an area, its top edge.'),
    doc_x: FractionSchema.describe('Where across the whole page, 0 to 1 — used when the selector finds nothing.'),
    doc_y: FractionSchema.describe('Where down the whole page, 0 to 1.'),
    w: ExtentSchema.optional().describe('An area rather than a point: its width, as a fraction of the element. Only with h.'),
    h: ExtentSchema.optional().describe('The area’s height, as a fraction of the element.'),
    quote: z
        .string()
        .min(1)
        .max(MOCKUP_QUOTE_MAX)
        .regex(/\S/, 'A quote is some of the page’s text, not only space.')
        .optional()
        .describe('Text rather than a point: the exact characters the comment is about, as the page has ' +
        'them. Only with quote_start.'),
    quote_start: z
        .int()
        .min(0)
        .max(10_000_000)
        .optional()
        .describe('Where the quote begins in the element’s text, counting characters from 0.'),
    viewport_w: z
        .int()
        .min(1)
        .max(MOCKUP_VIEWPORT_MAX)
        .optional()
        .describe('How wide the page was when the pin was placed, in CSS pixels.'),
});
/** Both of a pair, or neither — and an area and a quote are two different kinds of spot. */
export const MockupPinSchema = MockupPinFields.superRefine((pin, ctx) => {
    if ((pin.w === undefined) !== (pin.h === undefined)) {
        ctx.addIssue({ code: 'custom', path: ['w'], message: 'An area has a width and a height.' });
    }
    if ((pin.quote === undefined) !== (pin.quote_start === undefined)) {
        ctx.addIssue({
            code: 'custom',
            path: ['quote'],
            message: 'A quote says where it starts, and a start says what it quotes.',
        });
    }
    if (pin.w !== undefined && pin.quote !== undefined) {
        ctx.addIssue({
            code: 'custom',
            path: ['quote'],
            message: 'A pin is a point, an area or some text — not an area and text at once.',
        });
    }
}).describe('A spot on one page of a mockup version: a point, an area, or some text.');
const CommentBodySchema = z
    .string()
    .trim()
    .min(1)
    .max(MOCKUP_COMMENT_MAX)
    .describe('What you want to say about it. Anybody with the link reads it.');
/**
 * A comment's fields, shared by a member's body and a guest's. Session 55 adds `reply_to`: a
 * reply has no pin of its own — it is about what its thread is about.
 */
const MockupCommentFields = {
    version: z.int().min(1).describe('Which version the comment is about, counting from 1.'),
    body: CommentBodySchema,
    pin: MockupPinSchema.optional().describe('The spot it is about. Left out, the whole page.'),
    reply_to: z
        .uuid()
        .optional()
        .describe('Answer in a thread: the id of the thread’s first comment. A reply has no pin of its own.'),
};
function checkReply(input, ctx) {
    if (input.pin !== undefined && input.reply_to !== undefined) {
        ctx.addIssue({
            code: 'custom',
            path: ['pin'],
            message: 'A reply is about what its thread is about, so it has no pin of its own.',
        });
    }
}
/** The body of `POST /api/clients/me/mockups/:ref/comments`. */
export const MockupCommentInputSchema = z
    .strictObject(MockupCommentFields)
    .superRefine(checkReply)
    .describe('A comment on one version of a mockup, or a reply in one of its threads.');
/**
 * The body of `POST /api/mockups/link/:publicId/comments` — somebody with the link and no
 * account (session 49). A name, because a comment is a record of who said what.
 */
export const MockupGuestCommentInputSchema = z
    .strictObject({
    ...MockupCommentFields,
    name: z
        .string()
        .trim()
        .min(1)
        .max(MOCKUP_GUEST_NAME_MAX)
        // One line of ordinary characters: no control character (a newline included) and no
        // format character (a bidi override, a zero-width joiner) — a name is shown everywhere
        // the comment is, and nobody's is any of those.
        .regex(/^[^\p{Cc}\p{Cf}\u2028\u2029]+$/u, 'A name is one line of ordinary characters.')
        .describe('Your name, as everybody looking at the mockup will read it.'),
})
    .superRefine(checkReply)
    .describe('A comment from somebody with the link.');
/** The body of `POST /api/clients/me/mockups/:ref/review`. */
export const MockupReviewInputSchema = FileReviewSchema.extend({
    version: z.int().min(1).describe('Which version you are answering for, counting from 1.'),
}).describe('A client’s answer on a version the studio asked them to review: approve it, or ask for ' +
    'changes. One answer per version; the next version asks again.');
/* ------------------------------------------------------------------------ views */
export const MockupReviewViewSchema = z
    .object({
    state: FileReviewStateSchema,
    requested_at: Rfc3339Schema.nullable().describe('When the studio asked, or null.'),
    answered_at: Rfc3339Schema.nullable().describe('When the client answered, or null.'),
    answered_by: z.string().nullable().describe('Who answered, by name. Null while nobody has.'),
    note: z.string().describe('What the client said when they answered. Empty when nothing.'),
})
    .describe('Where one version stands in its review.');
export const MockupVersionSchema = z
    .object({
    number: z.int().min(1).describe('The version, counting from 1.'),
    note: z.string().describe('What the studio said about this version. Empty when nothing.'),
    url: z
        .string()
        .describe('This version’s page — public, with no sign-in, for as long as the mockup’s link is ' +
        'the current one.'),
    files: z.int().min(1).describe('How many files the version is made of.'),
    bytes: z.int().min(0).describe('Their size together, in bytes.'),
    uploaded_at: Rfc3339Schema.describe('When it was uploaded.'),
    review: MockupReviewViewSchema,
})
    .describe('One uploaded version of a mockup. Its files never change.');
/** What every comment says about itself — the first of a thread and a reply in it alike. */
const SaidFields = {
    id: z.string().describe('The comment’s id.'),
    body: z.string().describe('What was said.'),
    author: z.string().describe('Who said it, by name.'),
    from_studio: z.boolean().describe('True when somebody at the studio wrote it.'),
    guest: z
        .boolean()
        .describe('True when somebody with the link and no account wrote it, under a name they gave.'),
    created_at: Rfc3339Schema,
    hidden_at: Rfc3339Schema.nullable().describe('When the studio hid it — a guest’s words only. Hidden comments reach nobody but the studio.'),
};
export const MockupReplySchema = z
    .object(SaidFields)
    .describe('A reply in a comment’s thread (session 55).');
/**
 * A thread (session 55): the comment that started it — what it is about and whether it is
 * resolved — and every reply under it, oldest first. Resolving is the thread's, so a reply has
 * neither a pin nor a resolution of its own.
 */
export const MockupCommentSchema = z
    .object({
    ...SaidFields,
    id: z
        .string()
        .describe('The comment’s id — the thread’s. mockups_comment takes it as reply_to, and ' +
        'mockups_resolve_comment and mockups_reopen_comment as comment_id.'),
    version: z.int().min(1).describe('Which version it is about.'),
    resolved_at: Rfc3339Schema.nullable().describe('When the thread was resolved, or null while it is open.'),
    resolved_by: z
        .string()
        .nullable()
        .describe('Who resolved it, by name, or null — open, or resolved before names were kept.'),
    pin: MockupPinSchema.nullable().describe('The spot on the page it is about — a point, an area or some text — or null for the ' +
        'whole version.'),
    replies: z.array(MockupReplySchema).describe('The answers in its thread, oldest first.'),
})
    .describe('A comment on one version of a mockup, and its thread.');
export const MockupSchema = z
    .object({
    ref: MockupRefSchema,
    client: ClientSlugSchema,
    title: z.string().describe('What the design is.'),
    work_order_ref: WorkOrderRefSchema.nullable().describe('The project it belongs to, or null for one that stands alone.'),
    link: z
        .string()
        .describe('The link to send: anyone who has it can view the newest version, with no sign-in. ' +
        'Never listed and never indexed. mockups_rotate_link replaces it.'),
    version_count: z.int().min(0).describe('How many versions have been uploaded.'),
    latest: MockupVersionSchema.nullable().describe('The newest version, or null before the first upload.'),
    open_comments: z
        .int()
        .min(0)
        .describe('Threads still open — not resolved, and not hidden by the studio.'),
    archived_at: Rfc3339Schema.nullable().describe('When it was archived — taken off the client’s list and its link switched off — or null.'),
    created_at: Rfc3339Schema,
    updated_at: Rfc3339Schema.describe('When it last changed: a version, a comment or an answer.'),
})
    .describe('One mockup: a design shared with a client for feedback.');
export const MockupDetailSchema = MockupSchema.extend({
    versions: z.array(MockupVersionSchema).describe('Every version, newest first.'),
    comments: z
        .array(MockupCommentSchema)
        .describe('Every thread on every version, oldest first, each with its replies.'),
    review_url: z
        .string()
        .describe('Where a person reviews it — the page full-screen, with every comment where it was ' +
        'left (session 55). The same address as link, opened; it needs no sign-in.'),
}).describe('One mockup with all its versions and comments.');
/** `GET /api/clients/me/mockups`. */
export const ClientMockupsPageSchema = z
    .object({
    mockups: z.array(MockupSchema).describe('The client’s mockups, newest change first.'),
    awaiting_review: z
        .int()
        .min(0)
        .describe('Versions the studio asked about that this client has not answered yet.'),
})
    .describe('The client’s mockups page, and the count its nav badge shows.');
/** What `mockups_create_upload` answers. */
export const MockupUploadTicketSchema = z
    .object({
    mockup: MockupRefSchema,
    upload_url: z
        .string()
        .describe('PUT the bytes here — one .html file or one .zip with an index.html — with an ' +
        'ordinary HTTP request. Works once.'),
    expires_at: Rfc3339Schema.describe('When the URL stops working.'),
    max_bytes: z.int().describe('The most the upload may be.'),
})
    .describe('A one-time URL for the next version of a mockup.');
/** What `PUT /api/mockups/upload/:token` answers. */
export const MockupUploadResultSchema = z
    .object({
    mockup: MockupRefSchema,
    version: MockupVersionSchema,
    warnings: z
        .array(z.string())
        .describe('What will not work as it is — a path starting with "/" — each as a sentence. The ' +
        'version is stored either way.'),
})
    .describe('The version an upload became.');
/* ---------------------------------------------------------------- the link (49) */
/**
 * What the person looking may do there (session 55) — said by the server, so the review never
 * offers a button the service would refuse.
 */
const MayFields = {
    may_comment: z
        .boolean()
        .describe('May comment and reply. False for a seat that does not raise requests.'),
    may_resolve: z
        .boolean()
        .describe('May resolve and reopen a thread — the studio and a member, never a guest.'),
    may_hide: z.boolean().describe('May hide a guest’s words — the studio’s managers only.'),
};
/** Who is looking at a mockup's link, and where their own copy of it is. */
export const MockupLinkViewerSchema = z
    .discriminatedUnion('kind', [
    z.object({
        ...MayFields,
        kind: z.literal('guest'),
        signed_in: z
            .boolean()
            .describe('True for somebody signed in to the portal under another account, or none chosen — ' +
            'signing in again would change nothing, switching account would.'),
    }),
    z.object({
        ...MayFields,
        kind: z.literal('member'),
        ref: MockupRefSchema,
        portal_url: z.string().describe('The mockup in their portal, where they can answer it.'),
    }),
    z.object({
        ...MayFields,
        kind: z.literal('studio'),
        ref: MockupRefSchema,
        portal_url: z.string().describe('The mockup in the studio.'),
    }),
])
    .describe('Who is looking: somebody with no account here, a member of the client, or staff.');
/**
 * What `GET /api/mockups/link/:publicId` answers — the public viewer's whole read (session 49).
 * Everything here is what the link already shows or what the owner chose to share with it:
 * the design's title, its versions, and every comment the studio has not hidden. Since session
 * 55 it is the one place anybody reviews a mockup — the studio and the client's members too.
 */
export const MockupLinkSchema = z
    .object({
    title: z.string().describe('What the design is.'),
    versions: z
        .array(z.object({
        number: z.int().min(1),
        url: z.string().describe('The version’s own address — its entry page.'),
        note: z.string(),
        uploaded_at: Rfc3339Schema,
    }))
        .describe('Every version, newest first.'),
    comments: z
        .array(MockupCommentSchema)
        .describe('Every thread, oldest first, with its replies. Hidden words reach only the studio, ' +
        'marked; everybody else never sees them.'),
    viewer: MockupLinkViewerSchema,
})
    .describe('A mockup, as its link shows it.');
//# sourceMappingURL=mockups.js.map